/**
 * Constants and rules the server and browser must spell identically; divergence is silent — no
 * compiler or test compares the copies. A value belongs here only if both sides import it and it
 * needs zero dependencies.
 */

/** Estonian lower-casing: what the reader types and what the gazetteer stored fold through one
 * function, not two that agree today. */
export function fold(text: string): string {
  return text.trim().toLocaleLowerCase('et');
}

/** A list-valued filter's canonical form — trimmed, empties dropped, deduped, sorted — as one
 * definition rather than a convention five files follow. The URL, the request and the SQL bind all
 * carry this exact list, so one selection cannot have two spellings. */
export function canonicalList(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter((value) => value !== ''))].sort();
}

/** The one spelling every "this row has no value here" bucket travels under. The three filters
 * below are the same string by design — `labels.ts` reads a municipality's label under the county
 * sentinel — so splitting the values is a wire change, not a rename. */
const UNSPECIFIED = 'UNSPECIFIED';

/** `Filters.prefecture` sentinel for the rows with no prefecture at all (`prefecture IS NULL`);
 * the spelling is public — it travels as `?prefecture=UNSPECIFIED` in links readers have shared. */
export const UNSPECIFIED_PREFECTURE = UNSPECIFIED;

/** The four road users the traffic page filters by (PM decision P7); the chip group's order. */
export const ROAD_USERS = ['scooter', 'bicycle', 'motorcycle', 'pedestrian'] as const;

/** `TrafficFilters.county` sentinel for the 0.85 % of accidents with no county (`county IS NULL`),
 * selected exactly like {@link UNSPECIFIED_PREFECTURE} — mostly roads crossing several counties. */
export const UNSPECIFIED_COUNTY = UNSPECIFIED;

/** `Filters.municipalities` sentinel for the rows no municipality could be read out of
 * (`municipality IS NULL`), selectable inside the list exactly as the two sentinels above are. */
export const UNSPECIFIED_MUNICIPALITY = UNSPECIFIED;

/** The one timezone this project reasons in: every stored date is a Tallinn calendar day, so
 * rendering one in the reader's own zone would label an Estonian day with someone else's. */
export const TALLINN = 'Europe/Tallinn';

/** The ceiling `getIncidents` clamps every `limit` to. Here, not in `queries.ts`, because the browser sizes its own requests against it and that file reaches `node:fs` at module scope. */
export const MAX_PAGE_SIZE = 100;

/** The flags meaning "this euro figure is not one to publish" — both doubt the amount itself, and
 * no flag that doubts where or when joins them. Why, and what they cover: docs/design §6. */
export const UNRELIABLE_DAMAGE_FLAGS = ['money_mismatch', 'outlier_damage'] as const;
